import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Wind, 
  AlertTriangle, 
  Save, 
  Info, 
  CheckSquare, 
  Square,
  TrendingUp
} from 'lucide-react';
import { 
  useEnvironmentalRiskFactors, 
  useEnvironmentalRiskMultiplier 
} from '../../hooks/useEnvironmentalRiskFactors';
import './EnvironmentalAssessment.css';

/**
 * Environmental Assessment Component
 * Allows checking/unchecking environmental risk factors for a location
 * Can be used for departments, rooms, or embedded in other components
 */
const EnvironmentalAssessment = ({ 
  location, 
  departmentId,
  onUpdate,
  embedded = false,
  showMultiplierPreview = true,
  initialFactorIds = []
}) => {
  const { t } = useTranslation('patientRisk');
  const [selectedFactorIds, setSelectedFactorIds] = useState(initialFactorIds);
  const [filterCategory, setFilterCategory] = useState('all');
  const [saving, setSaving] = useState(false);
  
  // Load environmental factors applicable to this department
  const { factors: allFactors, loading: factorsLoading } = useEnvironmentalRiskFactors({ 
    activeOnly: true,
    departmentId: departmentId
  });
  
  // Calculate multiplier in real-time
  const { multiplier, loading: multiplierLoading } = useEnvironmentalRiskMultiplier(selectedFactorIds);
  
  // Initialize from location data if available
  useEffect(() => {
    if (location?.environmentalConditions?.activeFactorIds) {
      setSelectedFactorIds(location.environmentalConditions.activeFactorIds);
    } else if (initialFactorIds && initialFactorIds.length > 0) {
      setSelectedFactorIds(initialFactorIds);
    }
  }, [location, initialFactorIds]);
  
  const handleToggleFactor = (factorId) => {
    const isSelected = selectedFactorIds.includes(factorId);
    
    let newFactorIds;
    if (isSelected) {
      newFactorIds = selectedFactorIds.filter(id => id !== factorId);
    } else {
      newFactorIds = [...selectedFactorIds, factorId];
    }
    
    setSelectedFactorIds(newFactorIds);
    
    // Notify parent immediately (for embedded mode)
    if (onUpdate) {
      onUpdate({
        activeFactorIds: newFactorIds,
        multiplier: multiplier // Will update on next render
      });
    }
  };
  
  const handleSave = async () => {
    if (!onUpdate) return;
    
    setSaving(true);
    try {
      await onUpdate({
        activeFactorIds: selectedFactorIds,
        multiplier: multiplier,
        assessedAt: new Date(),
        assessedBy: 'current-user' // TODO: Get from auth context
      });
    } catch (error) {
      console.error('Error saving environmental assessment:', error);
      alert(t('failedToSaveAssessment'));
    } finally {
      setSaving(false);
    }
  };
  
  const getFilteredFactors = () => {
    if (filterCategory === 'all') {
      return allFactors;
    }
    return allFactors.filter(f => f.category === filterCategory);
  };
  
  const getFactorsByCategory = (category) => {
    return allFactors.filter(f => f.category === category);
  };
  
  const getRiskLevelFromMultiplier = (mult) => {
    if (mult >= 1.7) return { levelKey: 'riskLevelHigh', color: '#f44336' };
    if (mult >= 1.5) return { levelKey: 'riskLevelMedium', color: '#ff9800' };
    if (mult >= 1.3) return { levelKey: 'riskLevelLow', color: '#ffc107' };
    return { levelKey: 'riskLevelMinimal', color: '#4caf50' };
  };
  
  const riskLevel = getRiskLevelFromMultiplier(multiplier);
  const selectedFactors = allFactors.filter(f => selectedFactorIds.includes(f.id));
  const filteredFactors = getFilteredFactors();
  
  return (
    <div className={`environmental-assessment ${embedded ? 'embedded' : ''}`}>
      <div className="assessment-header">
        <div className="header-title">
          <Wind size={20} />
          <h3>{t('envRiskAssessment')}</h3>
        </div>
        {location && (
          <div className="location-info">
            {location.name || location.number || t('currentLocation')}
          </div>
        )}
      </div>
      
      {/* Current Risk Level Display */}
      {showMultiplierPreview && (
        <div className="environmental-multiplier-card">
          <div className="multiplier-display">
            <div className="multiplier-value">
              {multiplierLoading ? '...' : `×${multiplier.toFixed(2)}`}
            </div>
            <div className="multiplier-label">{t('environmentalMultiplier')}</div>
          </div>
          <div 
            className="risk-level-indicator"
            style={{ backgroundColor: riskLevel.color }}
          >
            {t(riskLevel.levelKey)}
          </div>
          <div className="risk-info">
            <Info size={14} />
            <span>
              {t('envFactorsSelected', { count: selectedFactorIds.length })}
              {multiplier > 1.0 && ` - ${t('riskIncreasedBy', { percent: ((multiplier - 1) * 100).toFixed(0) })}`}
            </span>
          </div>
        </div>
      )}
      
      {/* Category Filter */}
      <div className="assessment-filters">
        <label>{t('filterByCategory')}</label>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="category-filter"
        >
          <option value="all">{t('allCategoriesCount', { count: allFactors.length })}</option>
          <option value="Physical">{t('categoryCount', { category: t('categoryPhysical'), count: getFactorsByCategory('Physical').length })}</option>
          <option value="Operational">{t('categoryCount', { category: t('categoryOperational'), count: getFactorsByCategory('Operational').length })}</option>
          <option value="Infrastructure">{t('categoryCount', { category: t('categoryInfrastructure'), count: getFactorsByCategory('Infrastructure').length })}</option>
          <option value="Compliance">{t('categoryCount', { category: t('categoryCompliance'), count: getFactorsByCategory('Compliance').length })}</option>
        </select>
      </div>
      
      {/* Assessment Checklist */}
      <div className="assessment-checklist">
        <h4>{t('selectActiveEnvironmentalConditions')}</h4>
        
        {factorsLoading ? (
          <div className="loading">{t('loadingEnvironmentalFactors')}</div>
        ) : filteredFactors.length === 0 ? (
          <div className="no-factors">
            {t('noEnvironmentalFactorsForCategory')}
          </div>
        ) : (
          <div className="factors-checklist">
            {filteredFactors.map(factor => {
              const isSelected = selectedFactorIds.includes(factor.id);
              
              return (
                <label 
                  key={factor.id} 
                  className={`factor-checkbox ${isSelected ? 'selected' : ''}`}
                >
                  <div className="checkbox-icon">
                    {isSelected ? (
                      <CheckSquare size={20} color="#2196F3" />
                    ) : (
                      <Square size={20} color="#999" />
                    )}
                  </div>
                  
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggleFactor(factor.id)}
                    className="hidden-checkbox"
                  />
                  
                  <div className="factor-info">
                    <div className="factor-header-row">
                      <span className="factor-name">{factor.name}</span>
                      <span 
                        className="factor-multiplier"
                        style={{ 
                          backgroundColor: isSelected ? '#ff9800' : '#f5f5f5',
                          color: isSelected ? 'white' : '#999'
                        }}
                      >
                        ×{factor.multiplier.toFixed(1)}
                      </span>
                    </div>
                    
                    <div className="factor-meta">
                      <span className={`category-badge ${factor.category.toLowerCase()}`}>
                        {factor.category === 'Physical' ? t('categoryPhysical') : factor.category === 'Operational' ? t('categoryOperational') : factor.category === 'Infrastructure' ? t('categoryInfrastructure') : factor.category === 'Compliance' ? t('categoryCompliance') : factor.category}
                      </span>
                      {factor.seasonalVariation && (
                        <span className="seasonal-badge">☁️ {t('seasonalBadge')}</span>
                      )}
                    </div>
                    
                    {factor.description && (
                      <p className="factor-description">{factor.description}</p>
                    )}
                    
                    {factor.measurementCriteria && (
                      <p className="measurement-criteria">
                        <strong>{t('assessmentLabel')}</strong> {factor.measurementCriteria}
                      </p>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Selected Summary */}
      {selectedFactorIds.length > 0 && (
        <div className="selected-summary">
          <div className="summary-header">
            <AlertTriangle size={16} />
            <strong>{t('activeEnvironmentalConditionsCount', { count: selectedFactorIds.length })}</strong>
          </div>
          <div className="summary-list">
            {selectedFactors.map(factor => (
              <span key={factor.id} className="summary-tag">
                {factor.name} (×{factor.multiplier})
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Impact Summary */}
      {showMultiplierPreview && selectedFactorIds.length > 0 && (
        <div className="impact-summary">
          <TrendingUp size={16} />
          <div className="impact-text">
            <strong>{t('impactSummary', { value: Math.round(100 * multiplier) })}</strong>
            {multiplier >= 2.0 && t('impactCapped')}
          </div>
        </div>
      )}
      
      {/* Save Button (for standalone mode) */}
      {!embedded && (
        <div className="assessment-footer">
          <button
            onClick={handleSave}
            className="btn btn-save"
            disabled={saving}
          >
            {saving ? t('savingLabel') : (
              <>
                <Save size={16} />
                {t('saveEnvironmentalAssessment')}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default EnvironmentalAssessment;


